import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { distinctUntilChanged, Subject, switchMap, takeUntil } from 'rxjs';

import { CreditCardModalPage, CreditCardModalPageIdentifier } from '../../../modals/credit-card-modal/credit-card-modal.page';

import { PaymentClientId } from '../../../core/enums/payment-client-id.enum';
import { PaymentMethod } from '../../../core/enums/payment-method.enum';

import { NewCard } from '../../../core/models/new-card.model';
import { PaymentClient } from '../../../core/models/payment-client.model';
import { SavedCard } from '../../../core/models/saved-card.model';

import { CartService } from '../../../core/services/cart.service';
import { LoggerService } from '../../../core/services/logger.service';
import { ModalService } from '../../../core/services/modal.service';
import { OverlayService } from '../../../core/services/overlay.service';
import { SettingsService } from '../../../core/services/settings.service';
import { ValidationService } from '../../../core/services/validation.service';

@Component({
  selector: 'app-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PaymentComponent implements OnInit, OnDestroy {
  @Input()
  public savedCards: Array<SavedCard | NewCard>;

  @Output()
  public selectPaymentClient = new EventEmitter();

  @Output()
  public deleteSavedCard = new EventEmitter();

  @Output()
  public selectCard = new EventEmitter<SavedCard | NewCard>();

  public selectedCard: SavedCard | NewCard;
  public selectedPaymentClient: PaymentClient;
  public paymentClients: Array<PaymentClient>;
  public isTapV3Enabled: boolean;
  public creditCardDefaultImage: string;
  public cartTotal: number;

  // We use this to distinguish between TAP and Deema/ApplePay as they
  // all have the same paymentClient but a different paymentMethod value
  public tapPaymentClient: PaymentClient;

  public PaymentMethod = PaymentMethod;
  public PaymentClient = PaymentClientId;

  private unsubscribe$ = new Subject<void>();

  constructor(
    private validationService: ValidationService,
    private loggerService: LoggerService,
    private cartService: CartService,
    private settingsService: SettingsService,
    private translateService: TranslateService,
    private overlayService: OverlayService,
    private modalService: ModalService,
  ) {
    this.cartService.totalAfterDiscountsAndWalletCredits$.pipe(distinctUntilChanged(), takeUntil(this.unsubscribe$)).subscribe({
      next: (cartTotalAfterDiscountsAndWalletCredits) => {
        this.cartTotal = cartTotalAfterDiscountsAndWalletCredits;
      },
    });
  }

  @Input()
  public set availablePaymentClients(value: Array<PaymentClient>) {
    this.paymentClients = value;
    this.tapPaymentClient = this.paymentClients.find(
      (paymentClient) =>
        paymentClient.client === PaymentClientId.Tap &&
        paymentClient.paymentMethod !== PaymentMethod.ApplePay &&
        paymentClient.paymentMethod !== PaymentMethod.Deema,
    );

    this.creditCardDefaultImage = this.tapPaymentClient?.imageUrl;
  }

  ngOnInit() {
    this.isTapV3Enabled = this.settingsService.isTapV3FeatureEnabled();

    this.selectedCard = this.cartService.getSelectedCard();
    this.selectedPaymentClient = this.cartService.getSelectedPaymentClient();

    if (this.selectedCard && !this.savedCards.find((card) => card.cardId === this.selectedCard.cardId)) {
      this.cartService.setSelectedPaymentClient(this.paymentClients[0]);
      this.selectedCard = this.cartService.getSelectedCard();
      this.selectedPaymentClient = this.cartService.getSelectedPaymentClient();
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  public onSelectPaymentClient(paymentClient: PaymentClient): void {
    if (paymentClient && paymentClient.mov && this.cartTotal < paymentClient.mov) {
      return;
    }

    if (this.shouldShowCreditCardModal(paymentClient)) {
      void this.modalService
        .showModal<NewCard>({
          component: CreditCardModalPage,
          id: CreditCardModalPageIdentifier,
        })
        .then((newCard) => {
          if (!newCard) {
            return;
          }

          if (!newCard.shouldSave) {
            // We couldn't save the new card but we'll save it when the user places the order
            // so we'll just add it to the list and that's all
            this.savedCards = [...this.savedCards.filter((card) => card.cardId !== newCard.cardId), newCard];
            this.onSelectCard(newCard);
            return;
          }

          if (newCard.shouldSave) {
            void this.overlayService.showLoading().then(() => {
              this.cartService
                .saveCard(newCard.cardToken)
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe({
                  next: (savedCard: SavedCard) => {
                    void this.overlayService.hideLoading().then(() => {
                      if (!this.savedCards) {
                        this.savedCards = [];
                      }

                      this.savedCards = [...this.savedCards.filter((card) => card.cardId !== savedCard.cardId), savedCard];
                      this.onSelectCard(savedCard);
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    this.loggerService.error({ component: 'PaymentComponent', message: "couldn't save new card", error });

                    void this.overlayService.hideLoading().then(() => {
                      // We couldn't save the new card but we'll save it when the user places the order
                      // so we'll just add it to the list and that's all
                      this.savedCards = [...this.savedCards, newCard];
                      this.onSelectCard(newCard);
                    });
                  },
                });
            });
          }
        });

      return;
    }

    this.cartService.setSelectedPaymentClient(paymentClient);
    this.selectedCard = this.cartService.getSelectedCard();
    this.selectedPaymentClient = this.cartService.getSelectedPaymentClient();

    this.selectPaymentClient.emit();
  }

  public onSelectCard(card: SavedCard | NewCard): void {
    if (this.selectedCard && this.selectedCard.cardId === card.cardId) {
      return;
    }

    this.cartService.setSelectedPaymentClient(this.tapPaymentClient, { card });

    this.selectedCard = this.cartService.getSelectedCard();
    this.selectedPaymentClient = this.cartService.getSelectedPaymentClient();

    this.selectPaymentClient.emit();
    this.selectCard.emit(card);
  }

  public onDeleteSavedCard(card: SavedCard | NewCard, event: MouseEvent): void {
    event.stopPropagation();

    void this.modalService.showAlert({
      title: this.translateService.instant('CHECKOUT_PAGE.DELETE_CARD') as string,
      inlineButtons: true,
      buttons: [
        {
          text: this.translateService.instant('NO') as string,
        },
        {
          isDestructive: true,
          text: this.translateService.instant('YES') as string,
          handler: () => {
            if ('isNew' in card) {
              this.savedCards = this.savedCards.filter((c) => c.cardId !== card.cardId);
              this.selectDefaultPaymentMethodIfDeletedSelectedCard();
              return;
            }

            void this.overlayService.showLoading().then(() => {
              this.cartService
                .deleteSavedCard(card.cardId)
                .pipe(
                  switchMap(() => this.cartService.getSavedCards()),
                  takeUntil(this.unsubscribe$),
                )
                .subscribe({
                  next: (cards) => {
                    this.savedCards = cards;

                    void this.overlayService.hideLoading().then(() => this.selectDefaultPaymentMethodIfDeletedSelectedCard());
                  },
                  error: (error: HttpErrorResponse) => {
                    const errorMessage = this.validationService.getErrorMessage(error);
                    this.loggerService.error({ component: 'PaymentComponent', message: "couldn't get user saved cards", error });

                    void this.overlayService.hideLoading().then(() => {
                      void this.overlayService.showToast({ message: errorMessage, type: 'error', showCloseButton: true });
                    });
                  },
                });
            });
          },
        },
      ],
    });
  }

  public showCreditCardModal(paymentClient: PaymentClient): void {
    this.onSelectPaymentClient(paymentClient);
  }

  public maybeShowCreditCardModal(): boolean {
    if (this.shouldShowCreditCardModal(this.selectedPaymentClient) && !this.selectedCard) {
      this.showCreditCardModal(this.selectedPaymentClient);
      return true;
    }

    return false;
  }

  private shouldShowCreditCardModal(paymentClient: PaymentClient): boolean {
    // If TapV3 feature is NOT enabled, then the Tap payment client works as another payment
    // method — we'll redirect the user to the TAP portal when placing the order. But if the
    // feature is enabled, then it should only show the prompt that allows users to enter
    // credit cards but it cannot be selected since it doesn't work as a payment method
    return (
      this.isTapV3Enabled &&
      paymentClient.client === this.tapPaymentClient.client &&
      paymentClient.paymentMethod === this.tapPaymentClient.paymentMethod
    );
  }

  private selectDefaultPaymentMethodIfDeletedSelectedCard(): void {
    if (this.selectedCard && !this.savedCards.find((card) => card.cardId === this.selectedCard.cardId)) {
      this.cartService.setSelectedPaymentClient(this.paymentClients[0]);

      this.selectedCard = this.cartService.getSelectedCard();
      this.selectedPaymentClient = this.cartService.getSelectedPaymentClient();

      this.selectPaymentClient.emit();
      this.deleteSavedCard.emit();
    }
  }
}
